import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';
import { ChatProvider } from './context/ChatContext';
import { ContactProvider } from './context/ContactContext';
import { CallProvider } from './context/CallContext';
import { CookieConsentProvider } from './context/CookieConsentContext';
import { MeetingThemeProvider } from './components/MeetingThemes';
import Home from './pages/Home';
import About from './pages/About';
import Security from './pages/Security';
import Pricing from './pages/Pricing';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Auth from './components/Auth';
import Dashboard from './pages/Dashboard';
import Admin from './pages/Admin';
import OrganizationDashboard from './pages/OrganizationDashboard';
import VideoCall from './pages/VideoCall';
import ChatRoom from './pages/ChatRoom';
import Messages from './pages/Messages';
import WaitlistLanding from './pages/WaitlistLanding';
import Unsubscribe from './pages/Unsubscribe';
import EmailVerification from './components/EmailVerification';
import TermsAcceptanceModal from './components/TermsAcceptanceModal';
import CookieConsentBanner from './components/CookieConsentBanner';
import CookiePreferencesModal from './components/CookiePreferencesModal';
import { IncomingCallModal, ActiveCallScreen } from './components/call';

function ProtectedRoute({ children }) {
  const { user, userProfile, needsTermsReaccept } = useAuth();

  if (!user) return <Navigate to="/login" />;

  // Wait for userProfile to load - Show branded splash
  if (!userProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1c1c1e] via-[#000000] to-[#1c1c1e]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#06c] border-t-transparent rounded-full animate-spin mx-auto mb-8"></div>
          <h1 className="text-white text-3xl font-bold mb-2">Meet faster. Talk smarter.</h1>
          <p className="text-white/60 text-lg font-light">One app. Every conversation.</p>
        </div>
      </div>
    );
  }

  // Require email verification for email/password users only
  // OAuth users (Google, Apple) are automatically verified
  const isPasswordUser = user.providerData?.some(provider => provider.providerId === 'password');

  if (isPasswordUser && !userProfile.emailVerified) {
    return <EmailVerification />;
  }

  // Show terms acceptance modal if user needs to re-accept
  if (needsTermsReaccept) {
    return <TermsAcceptanceModal />;
  }

  // Note: iOS waitlist status is shown on Dashboard via WaitlistCard
  // Users can still use the website while pending iOS app access

  return children;
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/security" element={<Security />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/waitlist" element={<WaitlistLanding />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/login" element={!user ? <Auth /> : <Navigate to="/dashboard" />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/meet/:meetingId"
        element={
          <ProtectedRoute>
            <VideoCall />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat/:roomId"
        element={
          <ProtectedRoute>
            <ChatRoom />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/org/:orgSlug"
        element={
          <ProtectedRoute>
            <OrganizationDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages/:chatId"
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <MeetingThemeProvider>
        <ThemeProvider>
          <AuthProvider>
            <CookieConsentProvider>
              <ContactProvider>
                <ChatProvider>
                  <CallProvider>
                    <ToastProvider>
                      <AppRoutes />
                      {/* Global call UI components */}
                      <IncomingCallModal />
                      <ActiveCallScreen />
                      {/* Cookie consent UI */}
                      <CookieConsentBanner />
                      <CookiePreferencesModal />
                    </ToastProvider>
                  </CallProvider>
                </ChatProvider>
              </ContactProvider>
            </CookieConsentProvider>
          </AuthProvider>
        </ThemeProvider>
      </MeetingThemeProvider>
    </Router>
  );
}

export default App;
